import React, { useMemo } from 'react';
import { Button, Card, Col, Form, Row, Table } from 'react-bootstrap';
import { CalendarClock, Download, ReceiptText } from 'lucide-react';
import AdvanceDateRangePicker from '../common/AdvanceDateRangePicker';
import CustomMultiSelect from '../CustomMultiSelect';
import { CardSkeleton } from '../Loader';
import DataTable from '../common/data-table/DataTable';
import {
  ProfitabilityByClientTableSkeleton,
  ProfitabilityByServiceTypeSkeleton,
  RevenueForecastTableSkeleton,
  WorkingCapitalSkeleton
} from './FinancialIntelligenceSkeletons';
import { formatUsd } from '.';

const formatUsdTable = value =>
  new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(Number(value) || 0);

function escapeCsvCell(value) {
  const s = String(value ?? '');
  if (/[",\n\r]/.test(s)) {
    return `"${s.replace(/"/g, '""')}"`;
  }
  return s;
}

const statToneStyles = {
  revenue: {
    iconColor: '#212529',
    iconBg: '#f3f4f6',
    valueColor: '#212529',
    accentColor: '#6c757d'
  },
  success: {
    iconColor: '#198754',
    iconBg: '#22ff5617',
    valueColor: '#198754',
    accentColor: '#198754'
  },
  warning: {
    iconColor: '#2563EB',
    iconBg: '#3B82F61f',
    valueColor: '#2563EB',
    accentColor: '#2563EB'
  },
  danger: {
    iconColor: '#dc3545',
    iconBg: '#dc35451f',
    valueColor: '#dc3545',
    accentColor: '#dc3545'
  }
};

export function DashboardFilterCard({ options, values, onOrganizationChange, dateRange, onDateRangeChange }) {
  return (
    <Card className="shadow-sm rounded mb-3">
      <Card.Body className="p-3">
        <div
          className="d-flex flex-column flex-md-row justify-content-center justify-content-md-start align-items-start"
          style={{ gap: 4 }}
        >
          <Form.Group className="mb-0">
            <Form.Label className="tinyFont text-muted font-weight-bold mb-1">Period</Form.Label>
            <AdvanceDateRangePicker
              value={dateRange}
              onChange={onDateRangeChange}
              rangeType={dateRange?.rangeType || 'This Month'}
              allowAllTime={false}
            />
          </Form.Group>

          <div style={{ minWidth: '330px' }}>
            <Form.Group className="mb-0">
              <Form.Label className="tinyFont text-muted font-weight-bold mb-1 ml-2">Client</Form.Label>
              <CustomMultiSelect
                id="financial-intelligence-organization-selector"
                onChange={onOrganizationChange}
                selectedOptions={values.client}
                options={options.client}
                placeholder="Select organizations..."
                showSelectAll={true}
                maxToShow={2}
                className="px-0 dashboard_multi_select_custom_style"
              />
            </Form.Group>
          </div>
        </div>
      </Card.Body>
    </Card>
  );
}

export function FinanceStatCard({ title, value, note, icon: Icon, tone, children, loading = false }) {
  const cardTone = statToneStyles[tone];

  return (
    <Card
      className="shadow-sm rounded h-100"
      style={{
        border: '1px solid #e5e7ea'
      }}
    >
      <Card.Body className="p-3">
        {loading ? (
          <CardSkeleton isRect={true} height={100} width={200} />
        ) : (
          <>
            <div>
              <div>
                <div className="d-flex align-items-center mb-1">
                  {Icon && <Icon size={16} color={cardTone.iconColor} className="mr-2" />}

                  <div className="tinyFont text-muted mb-0">{title}</div>
                </div>
                <div
                  className="font-weight-bold xxlargeFont mb-1"
                  style={{ color: cardTone.valueColor, lineHeight: 1.1 }}
                >
                  {value}
                </div>
                {note ? (
                  <div className="xtinyFont" style={{ color: cardTone.accentColor, opacity: 0.8 }}>
                    {note}
                  </div>
                ) : null}
              </div>
            </div>
            {children}
          </>
        )}
      </Card.Body>
    </Card>
  );
}

export function ServiceSummaryCard({ title, items, loading = false }) {
  return (
    <FinanceStatCard title={title} value="" tone="revenue" loading={loading}>
      <div className="d-flex align-items-center mt-2">
        {items.map((item, index) => (
          <React.Fragment key={item.label}>
            {index > 0 ? (
              <div className="mx-4" style={{ alignSelf: 'stretch', width: 1, backgroundColor: '#e5e7ea' }} />
            ) : null}
            <div className="">
              <div className={`font-weight-bold xxlargeFont ${item.toneClass}`} style={{ lineHeight: 1.1 }}>
                {item.value}
              </div>
              <div className="xtinyFont text-muted">{item.label}</div>
            </div>
          </React.Fragment>
        ))}
      </div>
    </FinanceStatCard>
  );
}

function ProfitabilityMetricInline({ label, value, valueClassName }) {
  return (
    <span className="d-inline-flex flex-wrap align-items-baseline">
      <span className="tinyFont text-muted">{label}:</span>
      <span className={`smallFont font-weight-bold ml-1 ${valueClassName || 'text-dark'}`}>{value}</span>
    </span>
  );
}

function ProfitabilityServiceCard({ title, toneColor, metrics = [] }) {
  const summaryMetric = metrics.length > 0 ? metrics[metrics.length - 1] : null;
  const mainMetrics = metrics.length > 1 ? metrics.slice(0, -1) : [];

  return (
    <Col key={title} xs={12} lg={4} className="mb-3">
      <Card className="shadow-sm rounded h-100">
        <Card.Body className="p-3">
          <div className="d-flex align-items-center mb-3">
            <span
              className="rounded-circle mr-2"
              style={{
                width: 8,
                height: 8,
                backgroundColor: toneColor,
                boxShadow: `0 0 0 4px ${toneColor}1f`
              }}
            />
            <h6 className="mb-0 font-weight-bold">{title}</h6>
          </div>

          {mainMetrics.length === 6 ? (
            <>
              {[0, 1, 2].map(row => (
                <Row key={`${title}-row-${row}`} className="mb-2">
                  <Col xs={6} className="pr-2">
                    <ProfitabilityMetricInline
                      label={mainMetrics[row * 2].label}
                      value={mainMetrics[row * 2].value}
                      valueClassName={mainMetrics[row * 2].valueClassName}
                    />
                  </Col>
                  <Col xs={6} className="pl-2">
                    <ProfitabilityMetricInline
                      label={mainMetrics[row * 2 + 1].label}
                      value={mainMetrics[row * 2 + 1].value}
                      valueClassName={mainMetrics[row * 2 + 1].valueClassName}
                    />
                  </Col>
                </Row>
              ))}
            </>
          ) : (
            <Row>
              {mainMetrics.map(metric => (
                <Col key={`${title}-${metric.label}`} xs={6} className="mb-2">
                  <ProfitabilityMetricInline
                    label={metric.label}
                    value={metric.value}
                    valueClassName={metric.valueClassName}
                  />
                </Col>
              ))}
            </Row>
          )}

          {summaryMetric && (
            <div className="pt-2 mt-1" style={{ borderTop: '1px solid #eef2f7' }}>
              <ProfitabilityMetricInline
                label={summaryMetric.label}
                value={summaryMetric.value}
                valueClassName={summaryMetric.valueClassName}
              />
            </div>
          )}
        </Card.Body>
      </Card>
    </Col>
  );
}

export function ProfitabilityByServiceTypeSection({ services, loading = false }) {
  return (
    <Card className="shadow-sm rounded">
      <Card.Body className="p-3 p-lg-4">
        <h6 className="font-weight-bold mb-4">Profitability by Service Type</h6>
        {loading ? (
          <ProfitabilityByServiceTypeSkeleton />
        ) : (
          <Row>
            {services.map(service => (
              <ProfitabilityServiceCard key={service.title} {...service} />
            ))}
          </Row>
        )}
      </Card.Body>
    </Card>
  );
}

export function ProfitabilityByClientSection({
  rows = [],
  loading = false,
  pageNumber = 1,
  pageSize = 10,
  totalPages,
  resultCount,
  sortConfig = { key: 'clientName', direction: 'asc' },
  onSortChange,
  onBottomReached,
  loadingMoreData,
  dateRange,
  onDateRangeChange
}) {
  const headerLabel = label => <span className="font-weight-semibold tinyFont pb-2">{label}</span>;
  const columns = useMemo(
    () => [
      {
        key: 'clientName',
        label: headerLabel('Client'),
        sortable: true,
        align: 'left',
        render: value => <span className="py-3 text-dark smallFont">{value}</span>
      },
      {
        key: 'files',
        label: headerLabel('Files'),
        sortable: true,
        align: 'center',
        width: 70,
        render: value => <span className="py-3 text-right text-dark smallFont">{value}</span>
      },
      {
        key: 'services',
        label: headerLabel('Services'),
        sortable: true,
        align: 'center',
        width: 80,
        render: value => <span className="py-3 text-right text-dark smallFont">{value}</span>
      },
      {
        key: 'paid',
        label: headerLabel('Paid'),
        sortable: true,
        align: 'center',
        width: 70,
        render: value => <span className="py-3 text-right text-dark smallFont">{value}</span>
      },
      {
        key: 'cancelled',
        label: headerLabel('Cancelled'),
        sortable: true,
        align: 'center',
        width: 90,
        render: value => <span className="py-3 text-right text-dark smallFont">{value}</span>
      },
      {
        key: 'collectedFees',
        label: headerLabel('Collected Fees'),
        sortable: true,
        align: 'center',
        render: value => <span className={`py-3 text-right ${Number(value) < 0 ? 'text-danger' : 'text-dark'} smallFont`}>{formatUsdTable(value)}</span>
      },
      {
        key: 'hardCostsLost',
        label: headerLabel('HC Lost'),
        sortable: true,
        align: 'center',
        width: 100,
        render: value => <span className="py-3 text-right text-dark smallFont">{formatUsdTable(value)}</span>
      },
      {
        key: 'operatingResult',
        label: headerLabel('Operating Result'),
        sortable: true,
        align: 'center',
        width: 120,
        render: value => {
          const op = Number(value) || 0;
          return (
            <span
              className={`py-3 text-right font-weight-bold smallFont text-nowrap ${op >= 0 ? 'text-success' : 'text-danger'
                }`}
            >
              {formatUsdTable(value)}
            </span>
          );
        }
      },
      {
        key: 'workingCapital',
        label: headerLabel('Working Capital'),
        sortable: true,
        align: 'center',
        width: 120,
        render: value => <span className="py-3 text-right text-dark smallFont">{formatUsdTable(value)}</span>
      }
    ],
    []
  );

  const tableRows = useMemo(
    () =>
      rows.map((row, idx) => ({
        ...row,
        id: row.clientId ?? `${row.clientName}-${idx}`
      })),
    [rows]
  );

  const handleExport = () => {
    if (!rows.length) {
      return;
    }
    const header = [
      'Client',
      'Files',
      'Services',
      'Paid',
      'Cancelled',
      'Processing Fees',
      'HC Lost',
      'Operating Result',
      'Working Capital'
    ];
    const lines = [
      header.map(escapeCsvCell).join(','),
      ...rows.map(r =>
        [r.clientName, r.files, r.services, r.paid, r.cancelled, r.processingFees, r.hardCostsLost, r.operatingResult, r.workingCapital]
          .map(escapeCsvCell)
          .join(',')
      )
    ];
    const blob = new Blob([lines.join('\n')], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `profitability-by-client-${new Date().toISOString().slice(0, 10)}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  return (
    <Card className="shadow-sm rounded mt-4">
      <Card.Body className="p-3 p-lg-4">
        <div className="d-flex align-items-center justify-content-between flex-wrap mb-3" style={{ gap: 8 }}>
          <h6 className="font-weight-bold mb-0">Profitability by Client</h6>
          <div className="d-flex align-items-center flex-wrap" style={{ gap: 8 }}>
            {onDateRangeChange && (
              <AdvanceDateRangePicker
                value={dateRange}
                onChange={onDateRangeChange}
                rangeType={dateRange?.rangeType || 'This Month'}
                allowAllTime={false}
              />
            )}
            <Button
              variant="outline-secondary"
              size="sm"
              className="d-inline-flex align-items-center font-weight-semibold tinyFont py-1 px-2"
              onClick={handleExport}
              disabled={loading || rows.length === 0}
            >
              <Download size={14} className="mr-1" />
              Export
            </Button>
          </div>
        </div>

        <div className="mt-2">
          {loading && rows.length === 0 ? (
            <ProfitabilityByClientTableSkeleton />
          ) : rows.length === 0 ? (
            <p className="text-muted text-center tinyFont mb-0 py-2">No client data for this period.</p>
          ) : (
            <DataTable
              key={`${sortConfig.key}-${sortConfig.direction}`}
              data={tableRows}
              columns={columns}
              rowKey="id"
              pagination={false}
              sortable={true}
              defaultSort={sortConfig}
              onSortChange={onSortChange}
              pageSize={pageSize}
              serverPagination={true}
              striped={false}
              hover={true}
              bordered={false}
              responsive={false}
              loading={loading}
              emptyMessage="No client data for this period."
              maxHeight="calc(100vh - 60vh)"
              onBottomReached={onBottomReached}
              loadingMoreData={loadingMoreData}
              bottomOffset={100}
            />
          )}
          {tableRows?.length && (
            <div className="d-flex justify-content-end pt-2">
              {tableRows?.length} of {resultCount} clients
            </div>
          )}
        </div>
      </Card.Body>
    </Card>
  );
}

export function WorkingCapitalSection({ summaryCards, rows, loading = false }) {
  return (
    <Card className="shadow-sm rounded mt-4">
      <Card.Body className="p-3 p-lg-4">
        <div className="d-flex align-items-start mb-3">
          <ReceiptText size={16} className="mr-2 mt-1 text-dark" />
          <div>
            <h6 className="font-weight-bold mb-1">Working Capital Outstanding</h6>
            <p className="text-muted tinyFont mb-0">
              Hard costs tied up in delivered-unpaid services, money out there as working capital (COGS)
            </p>
          </div>
        </div>

        {loading ? (
          <WorkingCapitalSkeleton />
        ) : (
          <>
            <Row className="mb-3">
              {summaryCards.map(card => (
                <Col key={card.title} xs={12} lg={4} className="mb-3 mb-lg-0">
                  <Card className="rounded shadow-sm h-100">
                    <Card.Body className="py-3 px-4 text-center">
                      <div className="tinyFont text-muted mb-1">{card.title}</div>
                      <div className={`font-weight-bold xxlargeFont mb-0 ${card.valueClassName}`}>{card.value}</div>
                    </Card.Body>
                  </Card>
                </Col>
              ))}
            </Row>

            <div className="table-responsive">
              <Table borderless className="mb-0 smallFont">
                <thead>
                  <tr className="text-muted">
                    <th className="font-weight-semibold tinyFont pb-3">Service Type</th>
                    <th className="font-weight-semibold tinyFont pb-3 text-right">Unpaid Count</th>
                    <th className="font-weight-semibold tinyFont pb-3 text-right">Accounts Receivable</th>
                    <th className="font-weight-semibold tinyFont pb-3 text-right">Working Capital (HC)</th>
                    <th className="font-weight-semibold tinyFont pb-3 text-right">Pending Fees</th>
                  </tr>
                </thead>
                <tbody>
                  {rows.map(row => (
                    <tr key={row.serviceType} style={{ borderTop: '1px solid #eef2f7' }}>
                      <td className={`py-3 ${row.isTotal ? 'font-weight-bold' : ''} text-dark`}>{row.serviceType}</td>
                      <td className={`py-3 text-right ${row.isTotal ? 'font-weight-bold' : ''} text-dark`}>
                        {row.unpaidCount}
                      </td>
                      <td className={`py-3 text-right ${row.isTotal ? 'font-weight-bold' : ''} text-dark`}>
                        {formatUsd(row.accountsReceivable)}
                      </td>
                      <td
                        className={`py-3 text-right ${(row?.workingCapital || 0) < 0 ? 'text-danger' : 'text-info'} ${row.isTotal ? 'font-weight-bold' : ''
                          }`}
                      >
                        {formatUsd(row.workingCapital)}
                      </td>
                      <td
                        className={`py-3 text-right ${(row?.pendingFees || 0) < 0 ? 'text-danger' : 'text-success'} ${row.isTotal ? 'font-weight-bold' : ''
                          }`}
                      >
                        {formatUsd(row.pendingFees)}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </Table>
            </div>
          </>
        )}
      </Card.Body>
    </Card>
  );
}

export function RevenueForecastSection({ rows, loading = false }) {
  return (
    <Card className="shadow-sm rounded mt-4">
      <Card.Body className="p-3 p-lg-4">
        <div className="d-flex align-items-start mb-3">
          <CalendarClock size={16} className="mr-2 mt-1 text-dark" />
          <div>
            <h6 className="font-weight-bold mb-1">Revenue Forecast (Next 3 Months)</h6>
            <p className="text-muted tinyFont mb-0">
              Expected collections based on closing dates and estimated/actual fees on in-progress services
            </p>
          </div>
        </div>

        <div className="table-responsive">
          {loading ? (
            <RevenueForecastTableSkeleton />
          ) : (
            <Table borderless className="mb-0 smallFont">
              <thead>
                <tr className="text-muted">
                  <th className="font-weight-semibold tinyFont pb-3">Month</th>
                  <th className="font-weight-semibold tinyFont pb-3 text-right">Services</th>
                  <th className="font-weight-semibold tinyFont pb-3 text-right">Expected Revenue</th>
                  <th className="font-weight-semibold tinyFont pb-3 text-right">Est. Fees (Earnings)</th>
                  <th className="font-weight-semibold tinyFont pb-3 text-right">Est. Hard Costs (WC)</th>
                </tr>
              </thead>
              <tbody>
                {rows.map((row, idx) => (
                  <tr
                    key={row.isTotal ? 'revenue-forecast-total' : `revenue-forecast-${idx}`}
                    style={{ borderTop: '1px solid #eef2f7' }}
                  >
                    <td className={`py-3 text-dark ${row.isTotal ? 'font-weight-bold' : ''}`}>{row.month}</td>
                    <td className={`py-3 text-right text-dark ${row.isTotal ? 'font-weight-bold' : ''}`}>
                      {row.services}
                    </td>
                    <td className={`py-3 text-right ${(row.expectedRevenue || 0) < 0 ? 'text-danger' : 'text-dark'} ${row.isTotal ? 'font-weight-bold' : ''}`}>
                      {formatUsd(row.expectedRevenue)}
                    </td>
                    <td className={`py-3 text-right ${(row.estimatedFees || 0) < 0 ? 'text-danger' : 'text-success'} ${row.isTotal ? 'font-weight-bold' : ''}`}>
                      {formatUsd(row.estimatedFees)}
                    </td>
                    <td
                      className={`py-3 text-right ${row.isTotal ? 'font-weight-bold' : ''}`}
                      style={{ color: (row.estimatedHardCosts || 0) < 0 ? '#ff0000' : '#2563EB' }}
                    >
                      {formatUsd(row.estimatedHardCosts)}
                    </td>
                  </tr>
                ))}
              </tbody>
            </Table>
          )}
        </div>
      </Card.Body>
    </Card>
  );
}
